import { Icon } from '@assembly-js/design-system';
import { useTranslation } from 'react-i18next';
import {
  FiEdit as EditIcon,
  FiExternalLink as OpenPortalIcon,
  FiNavigation as NavigationIcon,
} from 'react-icons/fi';

import {
  COACHMARK_HIGHLIGHT_CLASS,
  COACHMARK_MENU_PROPS,
} from 'src/components/Coachmark';
import { DetailsIcon, TrashIcon } from 'src/components/icons';
import { useFeatureFlags } from 'src/context';
import { useIsStudioUser } from 'src/hooks/useIsStudioUser';
// eslint-disable-next-line no-restricted-imports
import { Action, BaseActionsMenuButton } from 'src/legacy/components/Dropdowns';

import { ClientActionsRendererProps } from './types';

export const ClientActionRenderer = ({
  data,
  handleClickRemove,
  handleClickEdit,
  handleClickInvite,
  handleClickCopyAccessLink,
  handleClickEmailAccessLink,
  handleClickViewDetails,
  handleClickTestAsClient,
  onActionMenuClick,
  isCoachmarkActive = false,
  onOpenPortalCoachmark,
}: ClientActionsRendererProps) => {
  const { t } = useTranslation();

  // Studio FTUX simplifies the menu to mirror the company menu: the standard
  // design-system edit icon, magic-link icons in the default menu text color, and no
  // redundant "View details" entry (row click already opens it). The flag keeps
  // today's menu for everyone else.
  const { EnableStudioFTUX } = useFeatureFlags();
  const isStudioUser = useIsStudioUser();

  const magicLinkIconClasses = `h-4 w-4${
    EnableStudioFTUX ? '' : ' text-gray-small'
  }`;

  const actions: Action[] = [
    {
      name: EnableStudioFTUX ? t(($) => $.actions.edit_client) : 'Edit info',
      icon: EnableStudioFTUX ? (
        <Icon icon="Edit" className="h-4 w-4" />
      ) : (
        <EditIcon />
      ),
      onClick: () => handleClickEdit(data),
    },
  ];
  if (data.status !== 'Active' && handleClickInvite) {
    actions.push({
      name: `${
        data.status === 'Inactive' ? 'Send invite' : 'Invite client'
      }...`,
      onClick: () => handleClickInvite(data),
      icon: <NavigationIcon />,
    });
  }

  if (handleClickEmailAccessLink) {
    actions.push({
      name: `Email magic link`,
      onClick: () => handleClickEmailAccessLink(data),
      icon: <Icon icon="Invite" className={magicLinkIconClasses} />,
    });
  }
  if (handleClickCopyAccessLink) {
    actions.push({
      name: `Copy magic link`,
      onClick: () => handleClickCopyAccessLink(data),
      icon: <Icon icon="Copy" className={magicLinkIconClasses} />,
    });
  }

  if (handleClickViewDetails && !EnableStudioFTUX) {
    actions.unshift({
      name: 'View details',
      icon: <DetailsIcon />,
      onClick: () => handleClickViewDetails(data),
    });
  }
  actions.push({
    name: t(($) => $.actions.delete_client),
    onClick: () => handleClickRemove(data),
    icon: <TrashIcon />,
    isDelete: true,
  });

  // Coachmark presentation, only while the tour's menu step is active and only on
  // the first row (the one the tour opens): a lit "Open portal" item on top, every
  // real action dimmed. Clicking "Open portal" completes the tour.
  const isCoachmarkMenu = isStudioUser;
  const menuActions: Action[] = isCoachmarkMenu
    ? [
        {
          name: t(($) => $.crm.coachmark.menu.open_portal),
          icon: <OpenPortalIcon />,
          onClick: () => {
            handleClickTestAsClient?.(data);
            onOpenPortalCoachmark?.();
          },
          classes: isCoachmarkActive ? [COACHMARK_HIGHLIGHT_CLASS] : [],
        },
        ...actions.map((action) => ({
          ...action,
          isDisabled: isCoachmarkActive,
        })),
      ]
    : actions;

  return (
    <BaseActionsMenuButton
      onActionMenuClick={onActionMenuClick}
      actions={menuActions}
      menuPropsOverride={
        isCoachmarkMenu && isCoachmarkActive ? COACHMARK_MENU_PROPS : undefined
      }
    />
  );
};
